import { Button } from "@/components/ui/button";
import { getDecimalCount } from '@/lib/currencies';
import { useEffect, useState } from "react";
import type { Plan } from "@/content/plans";
import { Badge } from "@/components/ui/badge"
import { IconThumbUpFilled, IconCircleCheckFilled } from "@tabler/icons-react"
import { CardTitle, CardDescription } from "@/components/ui/card"
import { useTranslation } from "react-i18next";
import { usePlans } from "@/hooks/use-plans";
import { usePromoCode } from "@/hooks/use-promo-code";
import { formatDiscount } from "@/lib/utils";

interface PlansProps {
  onPurchase?: (planId: string) => void;
  className?: string;
  titleKey?: string;
  subtitleKey?: string;
}

export default function Plans({ onPurchase, className, titleKey, subtitleKey }: PlansProps) {
  const { t, i18n } = useTranslation();
  const { plans } = usePlans();
  const { eventCode, referralCode } = usePromoCode();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // if (isLoading) {
  //   return <div className="text-center text-slate-400">({t('common.loading')})</div>;
  // }

  // Reorder plans for mobile - put popular plan first
  const orderedPlans = isMobile ? [...plans].sort((a, b) => (b.style.isPopular ? 1 : 0) - (a.style.isPopular ? 1 : 0)) : plans;

  return (
    <section className={`py-8 bg-slate-50 ${className}`} id="plans-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-10 lg:px-8 xl:px-0">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4 md:mb-6">
            {t(titleKey || 'purchase.title')}
          </h2>
          {subtitleKey && (
            <p className="text-base md:text-xl text-slate-600 mx-8 md:mx-auto max-w-xl">
              {t(subtitleKey)}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 md:gap-3 max-w-5xl mx-auto">
          {orderedPlans.map((plan: Plan) => {
            const trialPeriodDays = plan.pricing.trialPeriodDays;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl overflow-hidden ${plan.style.isPopular ? 'pt-8' : ''}
                ${plan.style.isPopular ? 'shadow-lg bg-white border' : 'border border-slate-200 bg-white md:mt-12 md:mb-8'
                  }
                `}
              >
                {plan.style.isPopular && (
                  <div className="absolute top-0 left-0 right-0 bg-sky-600 text-slate-50 py-1.5 text-center text-sm font-semibold border border-sky-600 flex items-center justify-center gap-1">
                    <IconThumbUpFilled className="text-slate-50" size={20} />
                    <span className="text-slate-50">{t('purchase.mostPopular')}</span>
                  </div>
                )}

                <div className="h-full pb-4 gap-4 flex flex-col overflow-hidden">
                  {/* Title */}
                  <div className={`relative overflow-hidden ${plan.style.isPopular ? 'aspect-[16/9]' : 'aspect-[2/1]'}`}>
                    <div className="absolute h-4 bg-sky-600 w-full"></div>
                    <div className="absolute bg-slate-900 w-full h-full rounded-tl-2xl rounded-tr-2xl"></div>
                    <img
                      src={plan.style.isPopular ?
                        'https://cdn-site.clarymind.com/product-page-app/plans/pro-annual-banner-bg-v2.jpg' :
                        'https://cdn-site.clarymind.com/product-page-app/plans/pro-3_month-banner-bg-v2.jpg'
                      }
                      alt=""
                      className={`absolute inset-0 w-full h-full object-cover rounded-tl-2xl rounded-tr-2xl ${plan.style.isPopular ? "opacity-35" : "opacity-45"}`}
                    />

                    <div className="relative flex flex-col items-start justify-center gap-2 px-6 h-full">
                      {plan.tags && plan.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-1">
                          {plan.tags.map((tag, index) => (
                            <span key={index} className={`${tag.backgroundColor} ${tag.textColor} px-3 py-1 rounded-full text-xs font-semibold`}>
                              {t(tag.text)}
                            </span>
                          ))}
                        </div>
                      )}

                      <CardTitle className={`text-2xl font-semibold text-slate-50 px-1`}>{t(plan.name)}</CardTitle>

                      <div className={`${plan.style.isPopular ? 'bg-slate-50 text-slate-900' : 'border border-slate-50 bg-slate-900/20 backdrop-blur-sm text-slate-50'} px-4 pt-1 pb-1.5 mt-1 rounded-md order-2 md:order-1`}>
                        <span className="text-sm font-semibold">{t(plan.duration.label)}</span>
                      </div>

                      {plan.description && plan.pricing.discount?.percentage !== undefined && (() => {
                        const code = eventCode || 'WEBSITE';
                        const promoKey = `promo.${code}.name`;
                        const promoName = t(promoKey) as string;
                        const discount = formatDiscount(plan.pricing.discount.percentage, i18n.language);
                        const text = promoName !== promoKey ? `${promoName} ${discount}` : t(plan.description, { discount });
                        return (
                          <CardDescription className={`px-1 text-sm font-semibold text-slate-300`}>
                            {text}
                          </CardDescription>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="flex flex-col items-start justify-center gap-2 pl-7  pt-2 pb-1">
                    <div className="flex items-baseline justify-center gap-2">
                      <span className={`text-3xl font-semibold ${plan.style.isPopular ? 'text-red-500' : 'text-slate-800'}`}>{plan.pricing.currency} {Number(plan.pricing.current).toLocaleString()}</span>
                      <span className={`text-sm ${plan.style.isPopular ? 'text-red-500' : 'text-slate-800'}`}> / {t(plan.unit)}</span>
                    </div>

                    {plan.pricing.original && (
                      <div className="flex gap-2 mb-0 items-baseline">
                        <span className="text-sm text-slate-400 line-through">{plan.pricing.currency} {Number(plan.pricing.original).toLocaleString()} / {t(plan.unit)}</span>
                        <span className="text-sm text-slate-500">
                          ({plan.pricing.currency} {(plan.pricing.current / plan.duration.months).toFixed(getDecimalCount(plan.pricing.currency)).toLocaleString()} / {t('purchase.perMonth')})
                        </span>
                        {/* <Badge className="text-xs bg-red-100 text-red-700 hover:bg-red-100">{t(plan.pricing.discount?.label || '') + ' ' + formatDiscount(plan.pricing.discount?.percentage || 0, i18n.language)}</Badge> */}
                      </div>
                    )}
                  </div>

                  {/* Separator */}
                  {/* <div className="w-full px-7">
                  <div className="h-[1px] bg-slate-100"></div>
                </div> */}

                  {/* Features */}
                  <ul className="space-y-3 flex-1 pl-6 pr-7 pb-3 pt-2">
                    {plan.features.map((feature: { text: string; isHighlighted: boolean; isPromo?: boolean }, index: number) => (
                      <li key={index} className={`flex items-start gap-2 leading-relaxed`}>
                        <IconCircleCheckFilled className={`w-6 h-6 ${feature.isPromo ? 'text-green-600' : feature.isHighlighted ? 'text-green-600' : 'text-lime-500'} shrink-0`} />
                        <span className={`text-base md:text-sm ${feature.isPromo ? 'text-slate-700 font-semibold' : feature.isHighlighted ? 'text-slate-700' : 'text-slate-500'}`}>{t(feature.text)}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Separator */}
                  {/* <div className="pt-0 pb-2 w-full my-1 px-6">
                  <div className="h-[1px] bg-slate-100"></div>
                </div> */}


                  {/* Button */}
                  <div className="px-6 pb-2 flex flex-col items-center justify-center gap-2">
                    <Button
                      className={`${plan.style.buttonStyle.background} ${plan.style.buttonStyle.textColor} ${plan.style.buttonStyle.hoverBackground} border-b-4 ${plan.style.buttonStyle.borderColor} text-base py-6 rounded-lg mt-auto w-full`}
                      onClick={() => {
                        onPurchase?.(plan.id);
                      }}
                    >
                      {trialPeriodDays
                        ? t('purchase.cta.startTrial', { days: trialPeriodDays })
                        : t(plan.cta)}
                    </Button>

                    {/** Referral code savings hint */}
                    {referralCode && (plan as any).pricing?.referralSavings != null && (plan as any).pricing.referralSavings > 0 && (
                      <div className="text-xs text-red-500 text-center">
                        {t('common.referralCodeApplied', {
                          code: referralCode,
                          currency: plan?.pricing.currency,
                          amount: Number((plan as any).pricing.referralSavings).toLocaleString()
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
